import { addWidgetsFromContext } from "@insite/client-framework/Configuration";
import { setPostStyleGuideTheme, setPreStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";

// Load all custom widgets so they are included in the bundle.
const widgets = require.context("./Widgets", true, /\.tsx$/);

// This line is required if you have any custom widgets that need to be loaded
// or if you are specifying any widget extensions in your blueprint.
const onHotWidgetReplace = addWidgetsFromContext(widgets);

// Enable hot module reloading for custom widgets.
if (module.hot) {
    module.hot.accept(widgets.id, () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

// Load all handlers so they are included in the bundle.
// Recursively get all handler modifications
const handlers = require.context("./Handlers", true);
// Load the handler modifications.
handlers.keys().forEach(key => handlers(key));

// Load all the extensions to the base widgets. This includes style extensions.
const widgetExtensions = require.context("./WidgetExtensions", true);
widgetExtensions.keys().forEach(key => widgetExtensions(key));

import "@tCooneyBrothers/Store/Reducers";
import { css } from "styled-components";

setPreStyleGuideTheme({
    // specifies secondary colors are editable
    colors: {
        secondary: {
            main: "#080",
        },
    },
});

setPostStyleGuideTheme({
    // specifies primary colors are not editable & fixed to these values
    typography: {
        fontFamilyImportUrl: "https://fonts.googleapis.com/css?family=Helvetica:300,400,600,700,800&display=swap",
        body: {
            fontFamily: "Helvetica, sans-serif",
        },
        headerPrimary: {
            fontFamily: "Helvetica, sans-serif",
        },
        headerSecondary: {
            fontFamily: "Helvetica, sans-serif",
            weight: 600,
        },
        headerTertiary: {
            fontFamily: "Helvetica, sans-serif",
        },
        h2: {
            fontFamily: "HelveticaCondensed",
        },
    },
    focus: {
        color: "#808080",
        width: "1px",
    },
    datePicker: {
        defaultProps: {
            cssOverrides: {
                datePicker: css`
                    > div[class^="react-datetime-picker"] {
                        > div > div[class="react-datetime-picker__inputGroup"] {
                            pointer-events: none;
                            > span[class="react-datetime-picker__inputGroup__leadingZero"] {
                                margin: 0;
                            }
                        }
                    }
                `,
                inputSelect: css`
                    width: 100%;
                `,
                formField: css`
                    padding-right: 10px;
                    position: relative;
                    label {
                        position: absolute;
                        z-index: 1;
                        left: 20px;
                        background: #fff;
                        width: auto;
                        top: 0px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                null,
                                null,
                                css`
                                    top: 2px;
                                `,
                                css`
                                    top: 2px;
                                `,
                                null,
                            ])}
                        min-height: auto;
                        & + div {
                            input,
                            select,
                            textarea {
                                background: #fff !important;
                                border-width: 1px !important;
                                font-size: 16px !important;
                            }
                        }
                    }
                `,
            },
        },
    },
    formField: {
        defaultProps: {
            cssOverrides: {
                formField: css`
                    position: relative;
                    label {
                        position: absolute;
                        z-index: 1;
                        left: 20px;
                        background: #fff;
                        width: auto;
                        top: 0px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                null,
                                null,
                                css`
                                    top: 2px;
                                `,
                                css`
                                    top: 2px;
                                `,
                                null,
                            ])}
                        min-height: auto;
                        & + div {
                            input,
                            select,
                            textarea {
                                background: #fff !important;
                                border-width: 1px !important;
                                font-size: 16px !important;
                            }
                        }
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                input:not(.react-datetime-picker__inputGroup__input),
                                select:not(.react-datetime-picker__inputGroup__input),
                                textarea,
                                .react-datetime-picker {
                                    font-size: 16px !important;
                                }
                            `,
                            css`
                                input:not(.react-datetime-picker__inputGroup__input),
                                select:not(.react-datetime-picker__inputGroup__input),
                                textarea,
                                .react-datetime-picker {
                                    font-size: 16px !important;
                                }
                            `,
                            css`
                                input:not(.react-datetime-picker__inputGroup__input),
                                select:not(.react-datetime-picker__inputGroup__input),
                                textarea,
                                .react-datetime-picker {
                                    font-size: 16px !important;
                                }
                            `,
                            css`
                                input:not(.react-datetime-picker__inputGroup__input),
                                select:not(.react-datetime-picker__inputGroup__input),
                                textarea,
                                .react-datetime-picker {
                                    font-size: 16px !important;
                                }
                            `,
                            null,
                        ])}                  
                `,
                inputSelect: css`
                    font-size: 16px !important;
                `,
            },
        },
    },
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: [0, 576, 768, 992, 1200],
        maxWidths: [540, 540, 732, 990, 1440],
    },
    modal: {
        defaultProps: {
            cssOverrides: {
                modalTitle: css`
                    border-bottom: 3px solid #ef3742;
                    background: #fafafa;
                `,
                modalBody: css`
                    .modal-action {
                        display: flex;
                        justify-content: initial;
                        align-items: center;
                        [class^="ButtonWrapper"] {
                            margin-left: auto;
                        }
                    }
                `,
            },
            closeButtonProps: {
                css: css`
                    padding: 0 6px;
                    border: 0;
                    background: transparent;
                `,
            },
        },
    },

    pagination: {
        defaultProps: {
            cssOverrides: {
                perPageSelect: css`
                    label {
                        padding: 0;
                        margin-right: 15px;
                        display: flex;
                        align-items: center;
                    }
                    select {
                        height: 30px;
                        border: 1px solid #cccccc !important;
                        border-radius: 20px !important;
                        padding-right: 25px !important;
                        font-size: 14px !important;
                        width: 60px !important;
                        ~ span {
                            top: -3px !important;
                            right: 6px !important;
                            padding: 0px !important;
                        }
                    }
                `,
                linkList: css`
                    button {
                        min-height: 20px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: auto;
                        padding: 4px 7px 3px;
                        border-radius: 50%;
                        span {
                            font-size: 12px;
                        }
                    }
                `,
            },
        },
    },

    tooltip: {
        defaultProps: {
            cssOverrides: {
                tooltipContainer: css`
                    z-index: 2;
                `,
            },
        },
    },

    dataTable: {
        defaultProps: {
            cssOverrides: {
                head: css`
                    border-bottom: 5px solid #ef3742;
                    span {
                        font-size: 18px;
                    }
                `,
            },
        },
    },
    button: {
        tertiary: {
            typographyProps: {
                color: "#000000",
            }
        }
    }
});
